import React, { useState, useEffect, useCallback } from 'react';
import { useChat } from '../../hooks/useChat';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import Icon from '../../components/AppIcon';
import EmirateSelector from './components/EmirateSelector';
import GenerationStatus from './components/GenerationStatus';
import ExecutiveSummary from './components/ExecutiveSummary';
import EmirateMetricsDashboard from './components/EmirateMetricsDashboard';
import ReportSections from './components/ReportSections';
import HistoricalReports from './components/HistoricalReports';
import ExportReport from './components/ExportReport';


const EMIRATE_LISTING_DATA = {
  all: {
    totalListings: 48200,
    avgPrice: 'AED 2,450,000',
    topAreas: ['Downtown Dubai', 'Palm Jumeirah', 'Yas Island', 'Al Reem Island', 'JVC'],
  },
  dubai: {
    totalListings: 18500,
    avgPrice: 'AED 3,200,000',
    topAreas: ['Downtown Dubai', 'Palm Jumeirah', 'Dubai Marina', 'Business Bay', 'JVC'],
  },
  'abu-dhabi': {
    totalListings: 9800,
    avgPrice: 'AED 2,100,000',
    topAreas: ['Yas Island', 'Saadiyat Island', 'Al Reem Island', 'Al Raha Beach', 'Corniche'],
  },
  sharjah: {
    totalListings: 7200,
    avgPrice: 'AED 850,000',
    topAreas: ['Al Majaz', 'Al Nahda', 'Muwaileh', 'Al Khan', 'Al Taawun'],
  },
  ajman: {
    totalListings: 4100,
    avgPrice: 'AED 520,000',
    topAreas: ['Al Nuaimia', 'Al Rashidiya', 'Emirates City', 'Al Jurf', 'Corniche Ajman'],
  },
  'ras-al-khaimah': {
    totalListings: 3800,
    avgPrice: 'AED 780,000',
    topAreas: ['Al Hamra Village', 'Mina Al Arab', 'Al Marjan Island', 'Julphar Towers', 'Al Nakheel'],
  },
  fujairah: {
    totalListings: 2900,
    avgPrice: 'AED 650,000',
    topAreas: ['Fujairah City', 'Dibba', 'Kalba', 'Al Faseel', 'Sakamkam'],
  },
  'umm-al-quwain': {
    totalListings: 1900,
    avgPrice: 'AED 420,000',
    topAreas: ['UAQ Marina', 'Al Salamah', 'Al Raas', 'Al Khor', 'Al Haditha'],
  },
};

const buildSystemPrompt = (emirate, period, listingData) => {
  const emirateName = emirate === 'all' ? 'All 7 UAE Emirates' : emirate?.replace(/-/g, ' ')?.replace(/\b\w/g, c => c?.toUpperCase());
  const data = listingData?.[emirate] || listingData?.all;
  return `You are a UAE real estate market intelligence analyst. Generate a comprehensive monthly market intelligence report for ${emirateName} for the period ${period}.

Real listing data context:
- Total active listings: ${data?.totalListings?.toLocaleString()}
- Average listing price: ${data?.avgPrice}
- Top areas: ${data?.topAreas?.join(', ')}

Generate a detailed JSON report with this EXACT structure (no markdown, pure JSON):
{
  "executiveSummary": "3-4 paragraph executive summary covering key market trends, price movements, and outlook",
  "keyHighlights": ["highlight 1", "highlight 2", "highlight 3", "highlight 4", "highlight 5"],
  "metrics": {
    "medianPrice": "AED X,XXX,XXX",
    "medianPriceTrend": "+X.X%",
    "yoyGrowth": "+X.X%",
    "transactions": "X,XXX",
    "transactionsTrend": "+X%",
    "pricePerSqft": "AED X,XXX",
    "pricePerSqftTrend": "+X.X%"
  },
  "emirateMetrics": [
    {"name": "Dubai", "yoyGrowth": 8.2, "medianPriceSqft": 1850},
    {"name": "Abu Dhabi", "yoyGrowth": 6.5, "medianPriceSqft": 1420},
    {"name": "Sharjah", "yoyGrowth": 5.1, "medianPriceSqft": 780},
    {"name": "Ajman", "yoyGrowth": 4.2, "medianPriceSqft": 520},
    {"name": "RAK", "yoyGrowth": 7.8, "medianPriceSqft": 680},
    {"name": "Fujairah", "yoyGrowth": 3.9, "medianPriceSqft": 590},
    {"name": "UAQ", "yoyGrowth": 3.1, "medianPriceSqft": 440}
  ],
  "supplyDemand": [
    {"month": "Aug", "supply": 72, "demand": 85},
    {"month": "Sep", "supply": 75, "demand": 88},
    {"month": "Oct", "supply": 78, "demand": 91},
    {"month": "Nov", "supply": 80, "demand": 94},
    {"month": "Dec", "supply": 76, "demand": 89},
    {"month": "Jan", "supply": 82, "demand": 96}
  ],
  "investmentHotspots": [
    {"area": "Area Name", "roi": "+X.X% ROI", "score": 92},
    {"area": "Area Name", "roi": "+X.X% ROI", "score": 87},
    {"area": "Area Name", "roi": "+X.X% ROI", "score": 83},
    {"area": "Area Name", "roi": "+X.X% ROI", "score": 79},
    {"area": "Area Name", "roi": "+X.X% ROI", "score": 74}
  ],
  "marketAnalysis": {
    "summary": "Detailed market analysis paragraph",
    "pricingForecasts": [
      {"segment": "Luxury Villas", "forecast": "+X%", "analysis": "Brief analysis"},
      {"segment": "Mid-Market Apartments", "forecast": "+X%", "analysis": "Brief analysis"},
      {"segment": "Affordable Housing", "forecast": "+X%", "analysis": "Brief analysis"},
      {"segment": "Off-Plan", "forecast": "+X%", "analysis": "Brief analysis"}
    ]
  },
  "reraUpdates": {
    "summary": "Summary of regulatory environment",
    "changes": [
      {"type": "New", "title": "Regulation title", "description": "Description", "effectiveDate": "Month YYYY"},
      {"type": "Update", "title": "Regulation title", "description": "Description", "effectiveDate": "Month YYYY"},
      {"type": "Reminder", "title": "Regulation title", "description": "Description", "effectiveDate": "Month YYYY"}
    ]
  },
  "offPlanUpdates": {
    "marketOverview": "Overview of off-plan market",
    "newLaunches": [
      {"projectName": "Project Name", "developer": "Developer", "location": "Location", "startingPrice": "AED X,XXX,XXX", "handover": "Q4 2027", "highlights": "Key features"},
      {"projectName": "Project Name", "developer": "Developer", "location": "Location", "startingPrice": "AED X,XXX,XXX", "handover": "Q2 2028", "highlights": "Key features"},
      {"projectName": "Project Name", "developer": "Developer", "location": "Location", "startingPrice": "AED X,XXX,XXX", "handover": "Q1 2027", "highlights": "Key features"}
    ]
  },
  "expatTrends": {
    "summary": "Summary of expat buying trends and visa-linked investment patterns",
    "topNationalities": [
      {"nationality": "Indian", "share": "28%"},
      {"nationality": "British", "share": "12%"},
      {"nationality": "Pakistani", "share": "9%"},
      {"nationality": "Russian", "share": "8%"}
    ],
    "visaInsights": [
      "Insight about AED 750K visa threshold",
      "Insight about AED 2M golden visa",
      "Insight about freehold zones",
      "Insight about ownership restrictions"
    ]
  }
}`;
};

const parseReportJSON = (text) => {
  try {
    const jsonMatch = text?.match(/\{[\s\S]*\}/);
    if (jsonMatch) return JSON.parse(jsonMatch?.[0]);
    return JSON.parse(text);
  } catch {
    return null;
  }
};

const UAEMarketIntelligenceReports = () => {
  const [selectedEmirate, setSelectedEmirate] = useState('all');
  const [selectedPeriod, setSelectedPeriod] = useState('2025-02');
  const [report, setReport] = useState(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [generatedEmirate, setGeneratedEmirate] = useState('');
  const [generatedPeriod, setGeneratedPeriod] = useState('');
  const navigate = useNavigate();

  const { response, isLoading, error, sendMessage } = useChat('OPEN_AI', 'gpt-4o', false);

  useEffect(() => {
    if (error) toast?.error('Failed to generate report. Please try again.');
  }, [error]);

  useEffect(() => {
    if (response && !isLoading && isGenerating) {
      const parsed = parseReportJSON(response);
      if (parsed) {
        setReport(parsed);
        setIsGenerating(false);
        toast?.success('Market intelligence report generated successfully!');
      } else {
        setIsGenerating(false);
        toast?.error('Could not parse report data. Please try again.');
      }
    }
  }, [response, isLoading]);

  const handleGenerate = useCallback(() => {
    setIsGenerating(true);
    setReport(null);
    setGeneratedEmirate(selectedEmirate);
    setGeneratedPeriod(selectedPeriod);

    const periodLabel = selectedPeriod === '2025-02' ? 'February 2025' :
      selectedPeriod === '2025-01' ? 'January 2025' :
      selectedPeriod === '2024-12' ? 'December 2024' :
      selectedPeriod === '2024-11' ? 'November 2024' :
      selectedPeriod === '2024-10' ? 'October 2024' : 'September 2024';

    const systemPrompt = buildSystemPrompt(selectedEmirate, periodLabel, EMIRATE_LISTING_DATA);

    sendMessage([
      { role: 'system', content: systemPrompt },
      { role: 'user', content: `Generate the complete UAE market intelligence report for ${selectedEmirate === 'all' ? 'All Emirates' : selectedEmirate} for ${periodLabel}. Return only valid JSON.` },
    ], { max_completion_tokens: 4000 });
  }, [selectedEmirate, selectedPeriod, sendMessage]);

  const handleLoadHistorical = (r) => {
    toast(`Loading ${r?.period} report...`, { icon: '📂' });
  };

  const emirateName = generatedEmirate === 'all' ? 'All Emirates' : generatedEmirate?.replace(/-/g,' ')?.replace(/\b\w/g, c => c?.toUpperCase());

  const periodLabel = generatedPeriod === '2025-02' ? 'February 2025' :
    generatedPeriod === '2025-01' ? 'January 2025' :
    generatedPeriod === '2024-12' ? 'December 2024' :
    generatedPeriod === '2024-11' ? 'November 2024' :
    generatedPeriod === '2024-10' ? 'October 2024' : 'September 2024';

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center gap-3 mb-4">
            <button
              onClick={() => navigate('/')}
              className="flex items-center gap-1.5 text-gray-500 hover:text-blue-600 transition-colors mr-1 group"
              aria-label="Back to Home"
            >
              <Icon name="ArrowLeft" size={18} className="group-hover:-translate-x-0.5 transition-transform" />
              <span className="text-sm font-medium">Back</span>
            </button>
            <div className="w-px h-6 bg-gray-200" />
            <div className="w-10 h-10 bg-blue-600 rounded-xl flex items-center justify-center">
              <Icon name="BarChart2" size={20} className="text-white" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-gray-900">UAE Market Intelligence Reports</h1>
              <p className="text-sm text-gray-500">AI-powered monthly analysis across all 7 emirates</p>
            </div>
            <div className="ml-auto flex items-center gap-1.5 bg-blue-50 border border-blue-200 rounded-full px-3 py-1">
              <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse" />
              <span className="text-xs font-medium text-blue-700">Powered by OpenAI</span>
            </div>
          </div>
          <EmirateSelector
            selectedEmirate={selectedEmirate}
            onEmirateChange={setSelectedEmirate}
            selectedPeriod={selectedPeriod}
            onPeriodChange={setSelectedPeriod}
            onGenerate={handleGenerate}
            isGenerating={isGenerating || isLoading}
          />
        </div>
      </div>
      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 py-6">
        <div className="grid grid-cols-1 xl:grid-cols-4 gap-6">
          {/* Main Report Area */}
          <div className="xl:col-span-3">
            <GenerationStatus
              isGenerating={isGenerating || isLoading}
              emirate={emirateName}
              period={periodLabel}
            />

            {!report && !isGenerating && !isLoading && (
              <div className="bg-white border-2 border-dashed border-gray-200 rounded-xl p-12 text-center">
                <div className="w-16 h-16 bg-blue-50 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <Icon name="Sparkles" size={28} className="text-blue-500" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Generate Your First Report</h3>
                <p className="text-gray-500 text-sm max-w-md mx-auto mb-6">
                  Select an emirate and report period above, then click "Generate New Report" to create a comprehensive AI-powered market intelligence report using real listing data.
                </p>
                <div className="flex flex-wrap justify-center gap-2">
                  {['Market Analysis', 'Pricing Forecasts', 'RERA Updates', 'Off-Plan Launches', 'Expat Trends']?.map((tag) => (
                    <span key={tag} className="bg-blue-50 text-blue-600 text-xs font-medium px-3 py-1 rounded-full border border-blue-100">{tag}</span>
                  ))}
                </div>
              </div>
            )}

            {report && (
              <>
                <ExportReport report={report} emirate={emirateName} period={periodLabel} />
                <ExecutiveSummary report={report} emirate={emirateName} period={periodLabel} />
                <EmirateMetricsDashboard report={report} />
                <ReportSections report={report} />
              </>
            )}
          </div>

          {/* Sidebar */}
          <div className="xl:col-span-1 space-y-4">
            {/* Quick Stats */}
            <div className="bg-white border border-gray-200 rounded-xl p-4">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">UAE Market Snapshot</p>
              <div className="space-y-3">
                {[
                  { label: 'Active Listings', value: '48,200+', icon: 'Home', color: 'blue' },
                  { label: 'Avg Transaction', value: 'AED 2.45M', icon: 'DollarSign', color: 'green' },
                  { label: 'Monthly Volume', value: '12,400+', icon: 'TrendingUp', color: 'purple' },
                  { label: 'Off-Plan Share', value: '38%', icon: 'Building2', color: 'orange' },
                ]?.map((stat) => (
                  <div key={stat?.label} className="flex items-center gap-3">
                    <div className={`w-8 h-8 bg-${stat?.color}-50 rounded-lg flex items-center justify-center`}>
                      <Icon name={stat?.icon} size={14} className={`text-${stat?.color}-600`} />
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">{stat?.label}</p>
                      <p className="text-sm font-bold text-gray-900">{stat?.value}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Emirates Coverage */}
            <div className="bg-white border border-gray-200 rounded-xl p-4">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Emirates Coverage</p>
              <div className="space-y-1.5">
                {[
                  { name: 'Dubai', listings: '18,500', flag: '🏙️' },
                  { name: 'Abu Dhabi', listings: '9,800', flag: '🕌' },
                  { name: 'Sharjah', listings: '7,200', flag: '🏛️' },
                  { name: 'Ajman', listings: '4,100', flag: '🌊' },
                  { name: 'Ras Al Khaimah', listings: '3,800', flag: '⛰️' },
                  { name: 'Fujairah', listings: '2,900', flag: '🏔️' },
                  { name: 'Umm Al Quwain', listings: '1,900', flag: '🌿' },
                ]?.map((e) => (
                  <div key={e?.name} className="flex items-center justify-between py-1">
                    <span className="text-sm text-gray-700">{e?.flag} {e?.name}</span>
                    <span className="text-xs text-gray-400">{e?.listings}</span>
                  </div>
                ))}
              </div>
            </div>

            <HistoricalReports onLoadReport={handleLoadHistorical} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default UAEMarketIntelligenceReports;
